package com.vizio.redwolf.auth.client

import com.vizio.redwolf.auth.api.CreatePinInfo
import com.vizio.redwolf.auth.api.DeletePinInfo
import com.vizio.redwolf.auth.api.HasPinInfo
import com.vizio.redwolf.auth.api.PinEnvironment
import com.vizio.redwolf.auth.api.UpdatePinInfo
import com.vizio.redwolf.auth.api.ValidatePinInfo
import com.vizio.redwolf.auth.model.CreatePinRequestBody
import com.vizio.redwolf.auth.model.DeletePinRequestBody
import com.vizio.redwolf.auth.model.HasPinRequestBody
import com.vizio.redwolf.auth.model.UpdatePinRequestBody
import com.vizio.redwolf.auth.model.ValidatePinRequestBody
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.CONTENT_TYPE_APP_JSON_UTF8
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.applyAppConfig
import com.vizio.redwolf.utils.applyTimeout
import com.vizio.redwolf.utils.applyUserBearerToken
import com.vizio.redwolf.utils.setTraceId
import com.vizio.redwolf.utils.safeHttpCall
import io.ktor.client.HttpClient
import io.ktor.client.request.delete
import io.ktor.client.request.headers
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpHeaders
import io.ktor.http.URLProtocol
import io.ktor.http.path
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.encodeToJsonElement

internal class PinHttpClient(private val pinEnv: PinEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig) : PinClient {

    private val httpClient: HttpClient = clientConfig.clientProvider.httpClient

    private val json = Json { encodeDefaults = true }

    override suspend fun hasPin(hasPinInfo: HasPinInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = HasPinRequestBody(accessToken = hasPinInfo.accessToken)
        val requestBodyJson = json.encodeToJsonElement(requestBody)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = pinEnv.proxyHost
                    path("${pinEnv.pinPath}/has-pay-pin")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    sentryHeader?.let { append("sentry-trace", it) }
                }
                applyUserBearerToken(hasPinInfo.accessToken)
                setBody(requestBodyJson)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun createPin(createPinInfo: CreatePinInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = CreatePinRequestBody(accessToken = createPinInfo.accessToken, pin = createPinInfo.pin)
        val requestBodyJson = json.encodeToJsonElement(requestBody)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = pinEnv.proxyHost
                    path("${pinEnv.pinPath}/create-pay-pin")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    sentryHeader?.let { append("sentry-trace", it) }
                }
                applyUserBearerToken(createPinInfo.accessToken)
                setBody(requestBodyJson)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun validatePin(validatePinInfo: ValidatePinInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = ValidatePinRequestBody(accessToken = validatePinInfo.accessToken, pin = validatePinInfo.pin)
        val requestBodyJson = json.encodeToJsonElement(requestBody)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = pinEnv.proxyHost
                    path("${pinEnv.pinPath}/validate-pay-pin")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    sentryHeader?.let { append("sentry-trace", it) }
                }
                applyUserBearerToken(validatePinInfo.accessToken)
                setBody(requestBodyJson)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun updatePin(updatePinInfo: UpdatePinInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = UpdatePinRequestBody(accessToken = updatePinInfo.accessToken, pin = updatePinInfo.pin)
        val requestBodyJson = json.encodeToJsonElement(requestBody)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = pinEnv.proxyHost
                    path("${pinEnv.pinPath}/update-pay-pin")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    sentryHeader?.let { append("sentry-trace", it) }
                }
                applyUserBearerToken(updatePinInfo.accessToken)
                setBody(requestBodyJson)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun deletePin(deletePinInfo: DeletePinInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = DeletePinRequestBody(accessToken = deletePinInfo.accessToken)
        val requestBodyJson = json.encodeToJsonElement(requestBody)
        return safeHttpCall {
            httpClient.delete {
                url {
                    protocol = URLProtocol.HTTPS
                    host = pinEnv.proxyHost
                    path("${pinEnv.pinPath}/delete-pay-pin")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    sentryHeader?.let { append("sentry-trace", it) }
                }
                applyUserBearerToken(deletePinInfo.accessToken)
                setBody(requestBodyJson)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

}